/**
 * Host power-up activity log (#232, v1, mock 09) — a lightweight "know what's happening"
 * feed so a HUMAN host stays oriented while the app resolves all power-up state. The
 * host never MANAGES power-ups; this is read-only awareness (a 2× award or a "why did
 * nobody buzz?" moment isn't a mystery).
 *
 * The store keeps only the most-recent play/grant (transient juice); {@link
 * usePowerupActivityLog} accumulates a short scrollback locally from that stream, keyed
 * by the store's monotonic `seq` so each event is appended exactly once.
 */

import { useEffect, useRef, useState } from 'react';
import type { PowerUpKind, Player } from '@jeopardy/shared';
import type { GameStoreSnapshot } from '@jeopardy/client-core';
import { POWERUP_CARD_META } from './PowerUpHand';

export interface PowerupLogItem {
  seq: number;
  kind: 'played' | 'granted';
  playerName: string;
  powerup: PowerUpKind;
}

/**
 * Insert one event into the log and keep it ordered by the store's global monotonic
 * `seq` DESCENDING — the true chronological order, since every power-up juice event is
 * stamped from one shared counter (game-store `powerupSeq`), so a higher `seq` always
 * means "happened later" across BOTH kinds. We must NOT rely on which effect (played vs
 * granted) prepended last: React runs effects in declaration order, so a play and a grant
 * reflected in the same commit would otherwise order by effect order, not by when they
 * happened (#299). Dedup is by `seq` alone (globally unique), which also stops an
 * unrelated re-render from re-appending an event that is no longer at the head.
 */
function addLogItem(
  prev: PowerupLogItem[],
  item: PowerupLogItem,
  max: number,
): PowerupLogItem[] {
  if (prev.some((p) => p.seq === item.seq)) return prev;
  return [item, ...prev].sort((a, b) => b.seq - a.seq).slice(0, max);
}

/**
 * Build a bounded, most-recent-first log from the store's latest play/grant events.
 * Depends only on the two event objects (stable references between unrelated updates),
 * reading the live roster through a ref so a plain score change never re-appends.
 * Ordering is by global `seq` (see {@link addLogItem}), never by effect/arrival order.
 */
export function usePowerupActivityLog(
  players: Player[],
  played: GameStoreSnapshot['lastPowerupPlayed'],
  granted: GameStoreSnapshot['lastPowerupGranted'],
  max = 8,
): PowerupLogItem[] {
  const [log, setLog] = useState<PowerupLogItem[]>([]);
  const playersRef = useRef(players);
  playersRef.current = players;

  const nameFor = (id: string): string =>
    playersRef.current.find((p) => p.id === id)?.name ?? 'A player';

  useEffect(() => {
    if (!played) return;
    setLog((prev) =>
      addLogItem(
        prev,
        {
          seq: played.seq,
          kind: 'played' as const,
          playerName: nameFor(played.playerId),
          powerup: played.powerup,
        },
        max,
      ),
    );
    // Only the event object (new per play) drives this; roster is read via ref.
  }, [played, max]);

  useEffect(() => {
    if (!granted) return;
    setLog((prev) =>
      addLogItem(
        prev,
        {
          seq: granted.seq,
          kind: 'granted' as const,
          playerName: nameFor(granted.playerId),
          powerup: granted.powerup,
        },
        max,
      ),
    );
  }, [granted, max]);

  return log;
}

/** Renders the host power-up activity feed (mock 09). Nothing if there's no activity. */
export function PowerUpHostLog({
  items,
}: {
  items: PowerupLogItem[];
}): React.JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div data-testid="host-powerup-log">
      <p className="host-log__title">Power-up activity</p>
      {items.map((item, i) => {
        const meta = POWERUP_CARD_META[item.powerup];
        return (
          <div
            key={item.kind + '-' + item.seq}
            className={'host-log__item' + (i === 0 ? ' host-log__item--live' : '')}
          >
            <span className="host-log__dot" aria-hidden="true">
              {item.kind === 'granted' ? '✦' : meta.icon}
            </span>
            <span className="host-log__txt">
              <b>{item.playerName}</b>{' '}
              {item.kind === 'granted'
                ? `unlocked a card (${meta.name})`
                : `played ${meta.name}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}
